'use client';

import { API_ROUTES, ApiError, HTTP_METHODS, useApiInvalidate, useApiMutation } from '@shared/api-utils';
import { useCallback } from 'react';

const NOT_FOUND_STATUS = 404;
const LAST_PAYMENT_METHOD_STATUS = 422;

type DeletePaymentMethodErrorReason = 'last_payment_method' | 'unknown';

type UseDeletePaymentMethodOptions = {
  instrumentId: string;
  onSuccess: () => void;
  onError: (reason: DeletePaymentMethodErrorReason) => void;
};

/**
 * Deletes a saved payment instrument and refreshes every consumer of the instruments list.
 *
 * A 404 means the instrument is already gone, so it is treated as a successful deletion.
 * A 422 means the player tried to delete their last available instrument, which the API rejects.
 * Both mean the cached list may be out of date (e.g. an instrument removed from another tab), so it is refreshed.
 */
function useDeletePaymentMethod({ instrumentId, onSuccess, onError }: UseDeletePaymentMethodOptions) {
  const { invalidateQuery } = useApiInvalidate();
  const deleteUrl = API_ROUTES.MY_ACCOUNT.DELETE_PAYMENT_INSTRUMENT.replace(':instrumentId', encodeURIComponent(instrumentId));

  const refreshPaymentMethods = useCallback(() => invalidateQuery(API_ROUTES.MY_ACCOUNT.PAYMENT_INSTRUMENTS), [invalidateQuery]);

  const handleDeleted = useCallback(() => {
    refreshPaymentMethods();
    onSuccess();
  }, [refreshPaymentMethods, onSuccess]);

  const { mutate, isPending } = useApiMutation<unknown, undefined>(deleteUrl, HTTP_METHODS.DELETE, {
    requestOptions: { credentials: true },
    mutationOptions: {
      onSuccess: handleDeleted,
      onError: error => {
        const status = error instanceof ApiError ? error.getStatus() : undefined;
        if (status === NOT_FOUND_STATUS) {
          handleDeleted();
          return;
        }
        if (status === LAST_PAYMENT_METHOD_STATUS) {
          refreshPaymentMethods();
          onError('last_payment_method');
          return;
        }
        onError('unknown');
      },
    },
  });

  const deletePaymentMethod = useCallback(() => mutate(undefined), [mutate]);

  return { deletePaymentMethod, isPending };
}

export { useDeletePaymentMethod, type DeletePaymentMethodErrorReason, type UseDeletePaymentMethodOptions };
